import { Trash2, Loader2, Plus, Minus, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ROUTES } from "@/shared/model/routes";
import { CountryFlag } from "@/shared/ui/country-flag";
import { Button } from "@/shared/ui/kit/button";
import { Image } from "@/shared/ui/kit/image";
import { Input } from "@/shared/ui/kit/input";
import { MetadataList } from "@/shared/ui/metadata-list";
import { Price } from "@/shared/ui/price";
import { ServiceIcon } from "@/shared/ui/service-icon";

import type { CartItemWithActions } from "../model/use-cart-model";
import { useQuantityInput } from "../model/use-quantity-input";

type CartItemProps = {
  item: CartItemWithActions;
  isPending?: boolean;
  onProductClick?: () => void;
};

export function CartItem({ item, isPending, onProductClick }: CartItemProps) {
  const { t } = useTranslation();
  const stock_quantity = item.product.quantity ?? 999;
  const imageUrl = item.product.image_url;

  // Convert metadata to string values for MetadataList
  const metadata: Record<string, string> | undefined = item.product.metadata
    ? Object.fromEntries(
        Object.entries(item.product.metadata)
          .filter(([_, v]) => v !== null && v !== undefined)
          .map(([k, v]) => [k, String(v)]),
      )
    : undefined;

  const quantityInput = useQuantityInput(
    item.quantity,
    item.onUpdateQuantity,
    isPending ?? false,
    stock_quantity,
  );

  return (
    <div className="group p-3 hover:bg-muted/40 transition-colors border-b border-border/50">
      {/* Top row: Image | Title + Metadata + Delete */}
      <div className="flex gap-3">
        {/* Thumbnail */}
        <div className="shrink-0 size-20 bg-muted rounded-lg overflow-hidden relative">
          <Link
            to={ROUTES.PRODUCT.replace(":id", item.product.id)}
            className="size-full block"
            onClick={onProductClick}
          >
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt={item.product.title}
                className="size-full object-contain"
              />
            ) : (
              <div className="size-full flex items-center justify-center text-muted-foreground/50">
                <Package className="size-6" />
              </div>
            )}
          </Link>

          {/* Badges overlay */}
          <div className="absolute top-1 right-1 flex gap-1 z-10">
            <div className="w-6 h-6 rounded-full bg-background/90 backdrop-blur-sm shadow-sm flex items-center justify-center">
              <CountryFlag
                countryCode={item.product.country ?? ""}
                className="text-sm"
              />
            </div>
            <div className="w-6 h-6 rounded-full bg-background/90 backdrop-blur-sm shadow-sm flex items-center justify-center">
              <ServiceIcon
                serviceId={item.product.product_type?.code ?? ""}
                className="w-3.5 h-3.5 text-muted-foreground"
              />
            </div>
          </div>
        </div>

        {/* Right side: Title + Metadata */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Header: Title + Remove */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0 pr-1">
              {item.product.product_type ? (
                <Link
                  to={ROUTES.PRODUCT.replace(":id", item.product.id)}
                  className="font-medium text-sm text-foreground hover:text-primary transition-colors line-clamp-2 leading-snug"
                  onClick={onProductClick}
                >
                  {item.product.title}
                </Link>
              ) : (
                <span className="font-medium text-sm text-foreground line-clamp-2 leading-snug">
                  {item.product.title}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 -mr-1.5 -mt-0.5 text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
              onClick={item.onRemove}
              disabled={isPending}
              aria-label={t("cart.removeItem", "Удалить товар")}
            >
              {isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Trash2 className="size-3.5" />
              )}
            </Button>
          </div>

          {/* Metadata */}
          <MetadataList metadata={metadata} variant="compact" maxVisible={3} />
        </div>
      </div>

      {/* Bottom row: Price + Stock + Quantity (under image) */}
      <div className="flex items-center justify-between gap-3 mt-3">
        {/* Price with total calculation */}
        <div className="flex items-baseline gap-1.5 text-sm">
          <span className="font-semibold">
            <Price cents={item.product.price_cents ?? 0} />
          </span>
          {quantityInput.localQuantity > 1 &&
            !isNaN(quantityInput.localQuantity) && (
              <>
                <span className="text-muted-foreground/60">×</span>
                <span className="text-muted-foreground/60 text-xs">
                  {quantityInput.localQuantity}
                </span>
                <span className="text-muted-foreground/60">=</span>
                <span className="font-semibold">
                  <Price
                    cents={
                      item.product.price_cents
                        ? item.product.price_cents * quantityInput.localQuantity
                        : 0
                    }
                  />
                </span>
              </>
            )}
        </div>

        {/* Stock indicator + Quantity controls */}
        <div className="flex items-center gap-2">
          {/* Stock status */}
          <span
            className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
              stock_quantity > 0
                ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10"
                : "text-destructive bg-destructive/10"
            }`}
          >
            {stock_quantity > 0
              ? t("cart.available", { count: stock_quantity })
              : t("cart.stock.outOfStock")}
          </span>

          {/* Quantity controls */}
          <div className="flex items-center rounded-md bg-background border">
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-muted-foreground hover:bg-muted"
              onClick={quantityInput.handleDecrement}
              disabled={quantityInput.localQuantity <= 1 || isPending}
            >
              <Minus className="size-4" />
            </Button>
            <Input
              type="text"
              inputMode="numeric"
              value={quantityInput.inputValue}
              onChange={quantityInput.handleInputChange}
              onBlur={quantityInput.handleInputBlur}
              onKeyDown={quantityInput.handleInputKeyDown}
              disabled={isPending}
              className="w-11 h-8 text-center text-base font-semibold p-0 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              min={1}
              max={stock_quantity}
            />
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-muted-foreground hover:bg-muted"
              onClick={quantityInput.handleIncrement}
              disabled={
                quantityInput.localQuantity >= stock_quantity || isPending
              }
            >
              <Plus className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
